import React, { useState, useEffect } from "react";
import API_URL from "../Config";
import axios from "axios";

import video from "./icon/video.svg";
import satellite from "./icon/satellite.svg";
import camera from "./icon/camera.svg";

import Swal from "sweetalert2";
import withReactContent from "sweetalert2-react-content";

import {
  useNavigate,
  useLocation,
  UNSAFE_NavigationContext as NavigationContext,
} from "react-router-dom";

import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";

import dayjs from "dayjs";
import "../App.css";

const MySwal = withReactContent(Swal);

const UploadContent = () => {
  const navigate = useNavigate();

  // ============================================================
  // PRE-RECORDED VIDEO STATES
  // ============================================================

  const [Movie_name, setMovie_name] = useState("");
  const [selectedVideoId, setSelectedVideoId] = useState(null);

  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [options, setOptions] = useState([]);
  const [filteredOptions, setFilteredOptions] = useState([]);

  // ============================================================
  // GENERAL STATES
  // ============================================================

  const [isSubmitted, setIsSubmitted] = useState(false);
  const [isDirty, setIsDirty] = useState(false);

  // ============================================================
  // INITIAL FORM DATA
  // ============================================================

  const initialFormData = {
    streamTitle: "",

    // Pre-recorded
    videoFile: null,

    // Satellite
    feedUrl: "",
    signalFormat: "",

    // Webcam
    webcamSource: "",

    // Common
    tags: "",
    thumbnail: null,
    description: "",
    scheduleStart: "",
    scheduleEnd: "",

    // Default selected type
    uploadType: "pre-recorded",

    category: "LIVE",
  };

  const [formData, setFormData] = useState(initialFormData);

  // ============================================================
  // FETCH PRE-RECORDED VIDEOS
  // ============================================================

  useEffect(() => {
    const fetchVideos = async () => {
      try {
        const res = await axios.get(`${API_URL}/api/v2/video/getall`);

        const movies = res.data.map((video) => ({
          id: video.id,
          title: video.videoTitle,
        }));

        setOptions(movies);
        setFilteredOptions(movies);
      } catch (err) {
        console.error("Error fetching videos:", err);
      }
    };

    fetchVideos();
  }, []);

  // ============================================================
  // HANDLE PRE-RECORDED DROPDOWN
  // ============================================================

  const handleDropdownToggle = () => {
    setDropdownOpen((prev) => !prev);
  };

  const handleInputChange = (e) => {
    const value = e.target.value;
    const lowerValue = value.toLowerCase();

    const startsWithValue = options.filter((option) =>
      option.title.toLowerCase().startsWith(lowerValue)
    );

    const containsValue = options.filter(
      (option) =>
        option.title.toLowerCase().includes(lowerValue) &&
        !option.title.toLowerCase().startsWith(lowerValue)
    );

    const filtered = [...startsWithValue, ...containsValue];

    setFilteredOptions(filtered);
    setDropdownOpen(true);

    setMovie_name(value);

    // User manually changed the text.
    // Clear selected ID because the typed value may not
    // correspond to the previously selected video.
    setSelectedVideoId(null);
  };

  const handleVideoSelect = (option) => {
    setMovie_name(option.title);
    setSelectedVideoId(option.id);
    setDropdownOpen(false);
  };

  // ============================================================
  // HANDLE GENERAL INPUTS
  // ============================================================

  const handleChange = (e) => {
    const { name, value, files } = e.target;

    setFormData((prev) => ({
      ...prev,
      [name]: files ? files[0] : value,
    }));
  };

  // ============================================================
  // HANDLE UPLOAD TYPE CHANGE
  // ============================================================

  const handleUploadTypeChange = (type) => {
    setFormData((prev) => ({
      ...prev,

      // Change selected type
      uploadType: type,

      // Reset type-specific values
      videoFile: null,
      feedUrl: "",
      signalFormat: "",
      webcamSource: "",
    }));

    // Reset pre-recorded selection
    setMovie_name("");
    setSelectedVideoId(null);
    setDropdownOpen(false);
  };

  // ============================================================
  // TYPES
  // ============================================================

  const types = [
    {
      id: "pre-recorded",
      title: "Pre-Recorded Video",
      desc: "Upload MP4, MOV, or other video files for scheduled streaming",
      icon: (
        <img
          src={video}
          alt="Video Icon"
          style={{ width: "60px", height: "50px" }}
        />
      ),
      btnText: "MP4, MOV, AVI, MKV",
    },

    {
      id: "satellite",
      title: "Satellite Feed",
      desc: "Connect to satellite sources for live broadcasting",
      icon: (
        <img
          src={satellite}
          alt="Satellite Icon"
          style={{ width: "60px", height: "50px" }}
        />
      ),
      btnText: "RTMP, HLS, WebRTC",
    },

    {
      id: "websources",
      title: "Webcam Stream",
      desc: "Start streaming directly from your camera",
      icon: (
        <img
          src={camera}
          alt="Camera Icon"
          style={{ width: "60px", height: "50px" }}
        />
      ),
      btnText: "Direct Camera Access",
    },
  ];

  // ============================================================
  // DIRTY FORM
  // ============================================================

  useEffect(() => {
    setIsDirty(
      JSON.stringify(formData) !== JSON.stringify(initialFormData)
    );
  }, [formData]);

  // ============================================================
  // BEFORE UNLOAD
  // ============================================================

  useEffect(() => {
    const handleBeforeUnload = (e) => {
      if (isDirty && !isSubmitted) {
        e.preventDefault();
        e.returnValue = "";
      }
    };

    window.addEventListener("beforeunload", handleBeforeUnload);

    return () => {
      window.removeEventListener("beforeunload", handleBeforeUnload);
    };
  }, [isDirty, isSubmitted]);

  // ============================================================
  // SAVE DRAFT
  // ============================================================

  const saveAsDraft = async () => {
    try {
      const data = new FormData();

      data.append(
        "title",
        formData.streamTitle || "Untitled"
      );

      data.append(
        "uploadType",
        formData.uploadType.toUpperCase()
      );

      data.append("status", "DRAFT");

      data.append("category", "LIVE");

      if (formData.description) {
        data.append("description", formData.description);
      }

      if (formData.tags) {
        data.append("tags", formData.tags);
      }

      if (formData.thumbnail) {
        data.append("image", formData.thumbnail);
      }

      await axios.post(
        `${API_URL}/api/streams/saveDraft`,
        data,
        {
          headers: {
            "Content-Type": "multipart/form-data",
          },
        }
      );

      Swal.fire(
        "Saved!",
        "Your draft was saved successfully.",
        "success"
      );
    } catch (err) {
      console.error("Error saving draft:", err);

      Swal.fire(
        "Error",
        "Could not save draft.",
        "error"
      );
    }
  };

  // ============================================================
  // NAVIGATION
  // ============================================================

  const handleClick = (link) => {
    if (isDirty && !isSubmitted) {
      MySwal.fire({
        title: "Save draft before leaving?",
        text: "You have unsaved changes.",
        icon: "warning",
        showCancelButton: true,
        confirmButtonText: "Yes, Save Draft",
        cancelButtonText: "No, Leave Anyway",
      }).then(async (result) => {
        if (result.isConfirmed) {
          await saveAsDraft();
        }

        navigate(link);
      });
    } else {
      navigate(link);
    }
  };

//   const handleSubmit = async (e) => {
//   e.preventDefault();

//   // Only handle Pre-Recorded Video here
//   if (formData.uploadType !== "pre-recorded") {
//     Swal.fire({
//       icon: "info",
//       title: "Not Implemented",
//       text: "Currently only Pre-Recorded Video is connected to the backend API.",
//     });
//     return;
//   }

//   // Validate selected video
//   if (!selectedVideoId) {
//     Swal.fire({
//       icon: "warning",
//       title: "Select Video",
//       text: "Please select a pre-recorded video.",
//     });
//     return;
//   }

//   // Validate thumbnail
//   if (!formData.thumbnail) {
//     Swal.fire({
//       icon: "warning",
//       title: "Thumbnail Required",
//       text: "Please select a thumbnail.",
//     });
//     return;
//   }

//   // Validate dates
//   if (!formData.scheduleStart || !formData.scheduleEnd) {
//     Swal.fire({
//       icon: "warning",
//       title: "Schedule Required",
//       text: "Please select both start and end time.",
//     });
//     return;
//   }

//   // Validate end time
//   if (
//     new Date(formData.scheduleEnd) <=
//     new Date(formData.scheduleStart)
//   ) {
//     Swal.fire({
//       icon: "warning",
//       title: "Invalid Time",
//       text: "End time must be after start time.",
//     });
//     return;
//   }

//   setIsSubmitted(true);

//   try {
//     const data = new FormData();

//     // =========================================================
//     // BACKEND PARAMETER NAMES
//     // =========================================================

//     data.append(
//       "StreamTitle",
//       formData.streamTitle
//     );

//     data.append(
//       "VideoId",
//       selectedVideoId
//     );

//     // Your Java enum should contain this value
//     data.append(
//       "status",
//       "UPCOMING"
//     );

//     data.append(
//       "tag",
//       formData.tags || ""
//     );

//     data.append(
//       "description",
//       formData.description || ""
//     );

//     // Spring LocalDateTime
//     data.append(
//       "startTime",
//       dayjs(formData.scheduleStart).format(
//         "YYYY-MM-DDTHH:mm:ss"
//       )
//     );

//     data.append(
//       "endTime",
//       dayjs(formData.scheduleEnd).format(
//         "YYYY-MM-DDTHH:mm:ss"
//       )
//     );

//     // Thumbnail
//     data.append(
//       "thumbnail",
//       formData.thumbnail
//     );

//     // =========================================================
//     // DEBUG
//     // =========================================================

//     console.log("Selected Video ID:", selectedVideoId);
//     console.log("Form Data:");

//     for (let pair of data.entries()) {
//       console.log(pair[0], pair[1]);
//     }

//     // =========================================================
//     // API CALL
//     // =========================================================

//     const token = sessionStorage.getItem("tokenn");

//     const res = await axios.post(
//       `${API_URL}/api/live/addPreRecordedVideo`,
//       data,
//       {
//         headers: {
//           Authorization: token,
//           "Content-Type": "multipart/form-data",
//         },
//       }
//     );

//     console.log("Success:", res.data);

//     MySwal.fire({
//       icon: "success",
//       title: "Stream Scheduled!",
//       text: "Pre-recorded video has been scheduled successfully.",
//     });

//     // Reset form
//     setFormData(initialFormData);
//     setMovie_name("");
//     setSelectedVideoId(null);
//     setDropdownOpen(false);

//   } catch (err) {
//     console.error("Upload Error:", err);

//     setIsSubmitted(false);

//     let errorMessage = "Something went wrong.";

//     if (err.response) {
//       console.error("Status:", err.response.status);
//       console.error("Response:", err.response.data);

//       if (typeof err.response.data === "string") {
//         errorMessage = err.response.data;
//       } else if (err.response.data?.message) {
//         errorMessage = err.response.data.message;
//       }
//     }

//     MySwal.fire({
//       icon: "error",
//       title: "Upload Failed",
//       text: errorMessage,
//     });
//   }
// };


const handleSubmit = async (e) => {
  e.preventDefault();

  // =========================================================
  // SATELLITE VALIDATION
  // =========================================================

  if (formData.uploadType === "satellite") {
    Swal.fire({
      icon: "info",
      title: "Not Implemented",
      text: "Satellite streaming is not implemented yet.",
    });
    return;
  }

  // =========================================================
  // COMMON VALIDATION
  // =========================================================

  if (!formData.streamTitle.trim()) {
    Swal.fire({
      icon: "warning",
      title: "Title Required",
      text: "Please enter a stream title.",
    });
    return;
  }

  if (!formData.thumbnail) {
    Swal.fire({
      icon: "warning",
      title: "Thumbnail Required",
      text: "Please select a thumbnail.",
    });
    return;
  }

  if (!formData.scheduleStart || !formData.scheduleEnd) {
    Swal.fire({
      icon: "warning",
      title: "Schedule Required",
      text: "Please select both start and end time.",
    });
    return;
  }

  if (
    new Date(formData.scheduleEnd) <=
    new Date(formData.scheduleStart)
  ) {
    Swal.fire({
      icon: "warning",
      title: "Invalid Time",
      text: "End time must be after start time.",
    });
    return;
  }

  // =========================================================
  // PRE-RECORDED VALIDATION
  // =========================================================

  if (formData.uploadType === "pre-recorded") {
    if (!selectedVideoId) {
      Swal.fire({
        icon: "warning",
        title: "Select Video",
        text: "Please select a pre-recorded video.",
      });
      return;
    }
  }

  // =========================================================
  // WEBCAM VALIDATION
  // =========================================================

  if (formData.uploadType === "websources") {
    if (!formData.webcamSource) {
      Swal.fire({
        icon: "warning",
        title: "Select Webcam",
        text: "Please select a webcam source.",
      });
      return;
    }
  }

  // =========================================================
  // AUTHENTICATION
  // =========================================================

  const token = sessionStorage.getItem("tokenn");

  const adminId = sessionStorage.getItem("adminId");

  if (formData.uploadType === "websources" && !adminId) {
    Swal.fire({
      icon: "error",
      title: "Admin ID Missing",
      text: "Unable to identify the logged-in admin.",
    });
    return;
  }

  // =========================================================
  // API CALL
  // =========================================================

  try {
    setIsSubmitted(true);

    const data = new FormData();

    let apiEndpoint = "";

    // =======================================================
    // PRE-RECORDED VIDEO
    // =======================================================

    if (formData.uploadType === "pre-recorded") {

      apiEndpoint = `${API_URL}/api/live/addPreRecordedVideo`;

      data.append("StreamTitle", formData.streamTitle);

      data.append("VideoId", selectedVideoId);

      data.append("status", "UPCOMING");

      data.append("tag", formData.tags || "");

      data.append("description", formData.description || "");

      data.append(
        "startTime",
        dayjs(formData.scheduleStart).format(
          "YYYY-MM-DDTHH:mm:ss"
        )
      );

      data.append(
        "endTime",
        dayjs(formData.scheduleEnd).format(
          "YYYY-MM-DDTHH:mm:ss"
        )
      );

      data.append("thumbnail", formData.thumbnail);
    }

    // =======================================================
    // WEBCAM LIVE STREAM
    // =======================================================

    else if (formData.uploadType === "websources") {

      // Change this path if your controller has a class-level
      // @RequestMapping prefix.
      apiEndpoint = `${API_URL}/api/streams/webcamlive`;

      data.append("title", formData.streamTitle);

      data.append("description", formData.description || "");

      data.append("category", "WEBCAMLIVE");

      data.append("tags", formData.tags || "");

      data.append("webcamSource", formData.webcamSource);

      data.append("adminId", adminId);

      data.append(
        "scheduledStart",
        dayjs(formData.scheduleStart).format(
          "YYYY-MM-DDTHH:mm:ss"
        )
      );

      data.append(
        "scheduledEnd",
        dayjs(formData.scheduleEnd).format(
          "YYYY-MM-DDTHH:mm:ss"
        )
      );

      data.append("thumbnailImage", formData.thumbnail);
    }

    // =======================================================
    // DEBUG FORM DATA
    // =======================================================

    console.log("Upload Type:", formData.uploadType);

    console.log("API Endpoint:", apiEndpoint);

    console.log("Form Data:");

    for (const pair of data.entries()) {
      console.log(pair[0], pair[1]);
    }

    // =======================================================
    // SEND REQUEST
    // =======================================================

    const res = await axios.post(apiEndpoint, data, {
      headers: {
        ...(token ? { Authorization: token } : {}),
      },
    });

    console.log("Upload Success:", res.data);

    // =======================================================
    // SUCCESS MESSAGE
    // =======================================================

    await MySwal.fire({
      icon: "success",
      title: "Stream Scheduled!",
      text:
        formData.uploadType === "websources"
          ? "Webcam live stream scheduled successfully."
          : "Pre-recorded video scheduled successfully.",
    });

    // =======================================================
    // RESET FORM
    // =======================================================

    setFormData({ ...initialFormData });

    setMovie_name("");

    setSelectedVideoId(null);

    setDropdownOpen(false);

    setIsSubmitted(false);

  } catch (err) {

    console.error("Upload Error:", err);

    setIsSubmitted(false);

    let errorMessage = "Something went wrong.";

    if (err.response) {

      console.error("Status:", err.response.status);

      console.error("Response:", err.response.data);

      if (typeof err.response.data === "string") {
        errorMessage = err.response.data;
      } else if (err.response.data?.message) {
        errorMessage = err.response.data.message;
      }
    }

    MySwal.fire({
      icon: "error",
      title: "Upload Failed",
      text: errorMessage,
    });
  }
};

  // ============================================================
  // JSX
  // ============================================================

  return (
    <div className="marquee-container">

      {/* ======================================================
          STREAM MANAGEMENT BUTTON
      ======================================================= */}

      <div className="AddArea">

        <button
          type="button"
          className="btn btn-custom"
          onClick={() =>
            handleClick("/admin/LiveStream")
          }
        >
          Stream Management
        </button>

      </div>

      <br />

      <div className="container3">

        {/* ====================================================
            BREADCRUMB
        ===================================================== */}

        <ol className="breadcrumb mb-4 d-flex my-0">

          <li className="breadcrumb-item text-white">
            Upload Content
          </li>

        </ol>

        <div className="outer-container">

          <div className="table-container p-4">

            {/* =================================================
                UPLOAD TYPE CARDS
            ================================================== */}

            <div className="upload-types mb-4">

              {types.map((type) => (

                <div
                  key={type.id}

                  className={`upload-card ${
                    formData.uploadType === type.id
                      ? "active"
                      : ""
                  }`}

                  onClick={() =>
                    handleUploadTypeChange(type.id)
                  }
                >

                  <div className="icon ms-1">
                    {type.icon}
                  </div>

                  <h5>
                    {type.title}
                  </h5>

                  <p>
                    {type.desc}
                  </p>

                  <button
                    type="button"
                    id="upload-content-button"
                  >
                    {type.btnText}
                  </button>

                </div>

              ))}

            </div>

            {/* =================================================
                FORM
            ================================================== */}

            <div id="live-stream-form">

              <form onSubmit={handleSubmit}>

                <div className="row">

                  {/* =================================================
                      LEFT SIDE
                  ================================================== */}

                  <div className="col-md-6">

                    {/* =================================================
                        STREAM TITLE
                    ================================================== */}

                    <div className="mb-3">

                      <label className="form-label">
                        Stream Title*
                      </label>

                      <input
                        type="text"
                        name="streamTitle"
                        value={formData.streamTitle}
                        onChange={handleChange}
                        className="form-control"
                        required
                      />

                    </div>

                    {/* =================================================
                        PRE-RECORDED VIDEO
                    ================================================== */}

                    {formData.uploadType === "pre-recorded" && (

                      <div
                        className="mb-3"
                        style={{
                          position: "relative",
                        }}
                      >

                        <label className="form-label">
                          Video File*
                        </label>

                        <div
                          style={{
                            position: "relative",
                          }}
                        >

                          <input
                            type="text"
                            value={Movie_name}
                            onClick={handleDropdownToggle}
                            onChange={handleInputChange}
                            className="form-control"
                            placeholder="Select Stream File"
                            style={{
                              paddingRight: "30px",
                            }}
                          />

                          <span
                            style={{
                              position: "absolute",
                              right: "10px",
                              top: "50%",
                              transform:
                                "translateY(-50%)",
                              pointerEvents: "none",
                              fontSize: "14px",
                              color: "#555",
                            }}
                          >
                            ▼
                          </span>

                        </div>

                        {/* =================================================
                            VIDEO DROPDOWN
                        ================================================== */}

                        {dropdownOpen &&
                          filteredOptions.length > 0 && (

                            <div
                              className="list-group position-absolute w-100"
                              style={{
                                maxHeight: "150px",
                                overflowY: "auto",
                                zIndex: 1000,
                                background: "white",
                                border:
                                  "1px solid #ccc",
                              }}
                            >

                              {filteredOptions.map(
                                (option) => (

                                  <button
                                    type="button"
                                    key={option.id}
                                    className="list-group-item list-group-item-action"
                                    onClick={() =>
                                      handleVideoSelect(
                                        option
                                      )
                                    }
                                  >
                                    {option.title}
                                  </button>

                                )
                              )}

                            </div>

                          )}

                      </div>

                    )}

                    {/* =================================================
                        SATELLITE FIELDS
                    ================================================== */}

                    {formData.uploadType === "satellite" && (

                      <>
                        {/* FEED URL */}

                        <div className="mb-3">

                          <label className="form-label">
                            Feed URL / Frequency*
                          </label>

                          <input
                            type="text"
                            name="feedUrl"
                            value={formData.feedUrl}
                            onChange={handleChange}
                            className="form-control"
                            placeholder="http://satellitefeed.example.com/channel1"
                            required
                          />

                        </div>

                        {/* SIGNAL FORMAT */}

                        <div className="mb-3">

                          <label className="form-label">
                            Signal Format*
                          </label>

                          <select
                            name="signalFormat"
                            value={formData.signalFormat}
                            onChange={handleChange}
                            className="form-control"
                            required
                          >

                            <option value="">
                              Select format
                            </option>

                            <option value="RTMP">
                              RTMP
                            </option>

                            <option value="HLS">
                              HLS
                            </option>

                            <option value="WebRTC">
                              WebRTC
                            </option>

                          </select>

                        </div>

                      </>

                    )}

                    {/* =================================================
                        WEBCAM FIELDS
                    ================================================== */}

                    {formData.uploadType === "websources" && (

                      <div className="mb-3">

                        <label className="form-label">
                          Webcam Source*
                        </label>

                        <select
                          name="webcamSource"
                          value={formData.webcamSource}
                          onChange={handleChange}
                          className="form-control"
                          required
                        >

                          <option value="">
                            Select webcam
                          </option>

                          <option value="frontCam">
                            Front Camera
                          </option>

                          <option value="backCam">
                            Back Camera
                          </option>

                          <option value="virtualCam">
                            Virtual Camera
                          </option>

                        </select>

                      </div>

                    )}

                    {/* =================================================
                        CATEGORY
                    ================================================== */}

                    <div className="mb-3">

                      <label className="form-label">
                        Category*
                      </label>

                      <input
                        type="text"
                        name="category"
                        value="LIVE"
                        className="form-control"
                        disabled
                      />

                    </div>

                    {/* =================================================
                        TAGS
                    ================================================== */}

                    {(formData.uploadType ===
                      "pre-recorded" ||
                      formData.uploadType ===
                        "websources") && (

                      <div className="mb-3">

                        <label className="form-label">
                          Tags*
                        </label>

                        <input
                          type="text"
                          name="tags"
                          value={formData.tags}
                          onChange={handleChange}
                          className="form-control"
                          placeholder="tech,demo,product"
                        />

                      </div>

                    )}

                  </div>

                  {/* =================================================
                      RIGHT SIDE
                  ================================================== */}

                  <div className="col-md-6">

                    {/* =================================================
                        DESCRIPTION
                    ================================================== */}

                    <div className="mb-3">

                      <label className="form-label">
                        Description
                      </label>

                      <textarea
                        name="description"
                        value={formData.description}
                        onChange={handleChange}
                        className="form-control"
                        rows="5"
                        placeholder="Describe your content..."
                      />

                    </div>

                    {/* =================================================
                        SCHEDULE START
                    ================================================== */}

                    <div className="mb-3">

                      <label className="form-label">
                        Schedule Start Time*
                      </label>

                      <input
                        type="datetime-local"
                        className="form-control"
                        value={formData.scheduleStart}
                        onChange={(e) =>
                          setFormData((prev) => ({
                            ...prev,
                            scheduleStart:
                              e.target.value,
                          }))
                        }
                        required
                      />

                    </div>

                    {/* =================================================
                        SCHEDULE END
                    ================================================== */}

                    <div className="mb-3">

                      <label className="form-label">
                        Schedule End Time*
                      </label>

                      <input
                        type="datetime-local"
                        className="form-control"
                        value={formData.scheduleEnd}
                        onChange={(e) =>
                          setFormData((prev) => ({
                            ...prev,
                            scheduleEnd:
                              e.target.value,
                          }))
                        }
                        required
                      />

                    </div>

                    {/* =================================================
                        THUMBNAIL
                    ================================================== */}

                    <div className="mb-3">

                      <label className="form-label">
                        Thumbnail*
                      </label>

                      <input
                        type="file"
                        name="thumbnail"
                        accept="image/*"
                        onChange={handleChange}
                        className="form-control"
                        required
                      />

                    </div>

                  </div>

                </div>

                {/* =================================================
                    BUTTONS
                ================================================== */}

                <div className="text-end mt-4">

                  <button
                    type="button"
                    className="btn btn-secondary m-4"
                    onClick={() =>
                      handleClick("/admin/LiveStream")
                    }
                  >
                    Cancel
                  </button>

                  <button
                    type="submit"
                    className="btn btn-custom me-4"
                  >
                    Start Upload
                  </button>

                </div>

              </form>

            </div>

          </div>

        </div>

      </div>

    </div>
  );
};

export default UploadContent;